import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/layout/Layout';
import { getITVisits, createITVisit, updateITVisit, updateITVisitStatus, getClients, getITLocations, getEmployees, getITAssetCategories, getITReferenceData, createITReferenceData, updateITReferenceData, deleteITReferenceData } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { useNavigate } from 'react-router-dom';
import { 
  CalendarCheck, Plus, MagnifyingGlass, Eye, PencilSimple,
  MapPin, Clock, User, ArrowRight, Play, CheckCircle, X
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import {
  DEFAULT_VISIT_PRIORITIES, DEFAULT_VISIT_TYPES, VISIT_STATUSES, visitFormToPayload, visitPriorityColor,
  visitStats, visitStatusColor, visitToForm,
} from '../../components/it-visits';
import { getITErrorMessage } from '../../lib/itErrors';

export default function ServiceVisitsPage() {
  const navigate = useNavigate();
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingVisit, setEditingVisit] = useState(null);
  const [form, setForm] = useState(visitToForm(null));
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterTipo, setFilterTipo] = useState('all');
  const [clients, setClients] = useState([]);
  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [visitTypes, setVisitTypes] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [selectedTechId, setSelectedTechId] = useState('');

  // Admin modal for Reference Data (Tipos y Prioridades)
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [adminCategory, setAdminCategory] = useState('visit_type'); // 'visit_type' | 'priority'
  const [adminItems, setAdminItems] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [editName, setEditName] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const loadVisits = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (debouncedSearch) params.search = debouncedSearch;
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterTipo !== 'all') params.tipo = filterTipo;
      const res = await getITVisits(params);
      setVisits(Array.isArray(res.data) ? res.data : (res.data?.items || []));
    } catch (err) {
      toast.error(getITErrorMessage(err, 'Error al cargar las visitas'));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, filterStatus, filterTipo]);

  useEffect(() => { loadVisits(); }, [loadVisits]);

  useEffect(() => {
    loadReferenceData();
  }, []);

  const loadReferenceData = async () => {
    try {
      const [clientsRes, empsRes, catsRes, visitTypesRes, prioritiesRes] = await Promise.all([
        getClients(),
        getEmployees(),
        getITAssetCategories(),
        getITReferenceData('visit_type'),
        getITReferenceData('priority')
      ]);
      setClients(clientsRes.data || []);
      setEmployees(empsRes.data || []);
      // Reference Data dinámico (nuevo endpoint /it/reference-data)
      const vt = visitTypesRes.data?.items || visitTypesRes.data || [];
      const pr = prioritiesRes.data?.items || prioritiesRes.data || [];
      setVisitTypes(vt.length > 0 ? vt : DEFAULT_VISIT_TYPES);
      setPriorities(pr.length > 0 ? pr : DEFAULT_VISIT_PRIORITIES);
    } catch (err) {
      console.error('Error loading reference data', err);
      // Fallback a valores por defecto si el endpoint falla
      setVisitTypes(DEFAULT_VISIT_TYPES);
      setPriorities(DEFAULT_VISIT_PRIORITIES);
    }
  };

  // === Funciones del Modal de Administración de Reference Data ===
  const openAdminModal = (category = 'visit_type') => {
    setAdminCategory(category);
    setShowAdminModal(true);
    setNewItemName('');
    setEditingItem(null);
    loadAdminItems(category);
  };

  const loadAdminItems = async (category) => {
    try {
      setAdminLoading(true);
      const res = await getITReferenceData(category);
      const items = res.data?.items || res.data || [];
      setAdminItems(items);
    } catch (err) {
      console.error('Error loading admin items', err);
      setAdminItems([]);
    } finally {
      setAdminLoading(false);
    }
  };

  const handleCreateItem = async () => {
    if (!newItemName.trim()) return;
    try {
      const name = newItemName.trim();
      const code = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
      await createITReferenceData({
        category: adminCategory,
        code: code || `item_${Date.now()}`,
        name,
        order: adminItems.length
      });
      setNewItemName('');
      loadAdminItems(adminCategory);
      loadReferenceData(); // refresca los dropdowns de la página principal
      toast.success('Elemento creado');
    } catch (err) {
      toast.error(getITErrorMessage(err, 'Error al crear'));
    }
  };

  const startEditItem = (item) => {
    setEditingItem(item);
    setEditName(item.name || item.nombre || '');
  };

  const handleUpdateItem = async () => {
    if (!editingItem || !editName.trim()) return;
    try {
      await updateITReferenceData(editingItem._id || editingItem.id, {
        name: editName.trim()
      });
      setEditingItem(null);
      setEditName('');
      loadAdminItems(adminCategory);
      loadReferenceData();
      toast.success('Elemento actualizado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar');
    }
  };

  const handleDeleteItem = async (item) => {
    if (!confirm(`¿Desactivar "${item.name || item.nombre}"?`)) return;
    try {
      await deleteITReferenceData(item._id || item.id);
      loadAdminItems(adminCategory);
      loadReferenceData();
      toast.success('Elemento desactivado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al desactivar');
    }
  };

  const cancelEdit = () => {
    setEditingItem(null);
    setEditName('');
  };

  const loadClientLocations = async (clientId) => {
    if (!clientId) { setLocations([]); return; }
    try {
      const res = await getITLocations({ client_id: clientId });
      setLocations(Array.isArray(res.data) ? res.data : (res.data?.items || []));
    } catch (err) { setLocations([]); }
  };

  const handleCreate = () => {
    setEditingVisit(null);
    setForm(visitToForm(null));
    setLocations([]);
    setShowModal(true);
  };

  const handleEdit = (visit) => {
    setEditingVisit(visit);
    setForm(visitToForm(visit));
    if (visit.client_id) loadClientLocations(visit.client_id);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_id) {
      toast.error('Seleccione un cliente');
      return;
    }
    if (!form.title || !form.title.trim()) {
      toast.error('El título de la visita es requerido');
      return;
    }
    try {
      const payload = visitFormToPayload(form);
      if (editingVisit) {
        await updateITVisit(editingVisit._id, payload);
        toast.success('Visita actualizada');
      } else {
        await createITVisit(payload);
        toast.success('Visita creada');
      }
      setShowModal(false);
      loadVisits();
    } catch (err) {
      toast.error(getITErrorMessage(err, 'Error al guardar la visita'));
    }
  };

  const handleStatusChange = async (visitId, newStatus) => {
    try {
      await updateITVisitStatus(visitId, { status: newStatus });
      toast.success(`Estado actualizado a: ${newStatus}`);
      loadVisits();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const addTechnician = () => {
    if (!selectedTechId) return;
    if (form.technicians.some(t => t.employee_id === selectedTechId)) {
      toast.error('Técnico ya asignado');
      return;
    }
    setForm(prev => ({
      ...prev,
      technicians: [...prev.technicians, { employee_id: selectedTechId, employee_name: employees.find((e) => e._id === selectedTechId)?.name || '', role: 'principal' }]
    }));
    setSelectedTechId('');
  };

  const removeTechnician = (empId) => {
    setForm(prev => ({
      ...prev,
      technicians: prev.technicians.filter(t => t.employee_id !== empId)
    }));
  };

  const updateForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (field === 'client_id') loadClientLocations(value);
  };

  const statusActions = (status) => {
    const actions = {
      programada: [
        { label: 'En Camino', status: 'en_camino', icon: ArrowRight },
        { label: 'Cancelar', status: 'cancelada', icon: X }
      ],
      en_camino: [
        { label: 'Check-In', status: 'en_sitio', icon: MapPin }
      ],
      en_sitio: [
        { label: 'Pausar', status: 'pausada', icon: Clock },
        { label: 'Completar', status: 'completada', icon: CheckCircle }
      ],
      pausada: [
        { label: 'Reanudar', status: 'en_sitio', icon: Play }
      ]
    };
    return actions[status] || [];
  };

  const counts = visitStats(visits);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <CalendarCheck className="text-[#E2B05E]" size={28} />
              Visitas Técnicas
            </h1>
            <p className="text-gray-400 text-sm mt-1">Gestión de visitas de servicio técnico</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleCreate} className="bg-[#E2B05E] hover:bg-[#c9993f] text-black">
              <Plus size={18} className="mr-2" /> Nueva Visita
            </Button>
            <Button 
              onClick={() => openAdminModal('visit_type')} 
              variant="outline" 
              className="border-gray-600 text-gray-300 hover:bg-gray-800"
            >
              <PencilSimple size={16} className="mr-2" /> Gestionar Tipos
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Total', value: counts.total, color: 'text-white' },
            { label: 'Programadas', value: counts.scheduled, color: 'text-blue-400' },
            { label: 'En Sitio', value: counts.onSite, color: 'text-purple-400' },
            { label: 'Completadas', value: counts.completed, color: 'text-green-400' },
            { label: 'Canceladas', value: counts.cancelled, color: 'text-red-400' }
          ].map((s, i) => (
            <div key={i} className="bg-[#1a1d23] border border-gray-800 rounded-lg p-3 text-center">
              <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
              <div className="text-xs text-gray-500">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <Input
              placeholder="Buscar por número, cliente..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-[#1a1d23] border-gray-700 text-white"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[160px] bg-[#1a1d23] border-gray-700 text-white">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent className="bg-[#1a1d23] border-gray-700">
              <SelectItem value="all">Todos</SelectItem>
              {VISIT_STATUSES.map(s => (
                <SelectItem key={s} value={s}>{s.replace(/_/g,' ')}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterTipo} onValueChange={setFilterTipo}>
            <SelectTrigger className="w-[180px] bg-[#1a1d23] border-gray-700 text-white">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent className="bg-[#1a1d23] border-gray-700">
              <SelectItem value="all">Todos</SelectItem>
              {visitTypes.map(vt => (
                <SelectItem key={vt.code} value={vt.code}>{vt.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Visits List */}
        <div className="space-y-3">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#E2B05E]"></div>
            </div>
          ) : visits.length === 0 ? (
            <div className="bg-[#1a1d23] border border-gray-800 rounded-lg text-center py-12 text-gray-500">
              <CalendarCheck size={48} className="mx-auto mb-3 opacity-50" />
              <p>No hay visitas registradas</p>
              <Button onClick={handleCreate} variant="ghost" className="mt-3 text-[#3AB8D6]">
                <Plus size={16} className="mr-1" /> Crear primera visita
              </Button>
            </div>
          ) : (
            visits.map((visit) => (
              <div key={visit._id} className="bg-[#1a1d23] border border-gray-800 rounded-lg p-4 hover:border-gray-700 transition-colors">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-white font-semibold">{visit.visit_number}</span>
                      <Badge className={visitStatusColor(visit.status)}>
                        {(visit.status || '').replace(/_/g, ' ')}
                      </Badge>
                      <Badge className={visitPriorityColor(visit.priority)}>
                        {visit.priority}
                      </Badge>
                    </div>
                    <div className="text-sm text-gray-400">
                      <span className="text-gray-300">{visit.client_name}</span>
                      {visit.location_name && <span> · {visit.location_name}</span>}
                      {visit.tipo && <span> · <span className="capitalize">{visit.tipo.replace(/_/g, ' ')}</span></span>}
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      {visit.scheduled_for && (
                        <span className="flex items-center gap-1">
                          <Clock size={12} /> {visit.scheduled_for.replace('T', ' ').slice(0, 16)}
                        </span>
                      )}
                      {visit.technicians?.length > 0 && (
                        <span className="flex items-center gap-1">
                          <User size={12} /> {visit.technicians.map(t => t.employee_name || t.employee_id).join(', ')}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <MapPin size={12} /> {visit.asset_count || 0} activos
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {statusActions(visit.status).map((action) => (
                      <Button
                        key={action.status}
                        size="sm"
                        variant="outline"
                        onClick={() => handleStatusChange(visit._id, action.status)}
                        className={`border-gray-600 text-gray-300 text-xs ${action.status === 'cancelada' ? 'hover:bg-red-500/20 hover:text-red-400' : 'hover:bg-[#3AB8D6]/20 hover:text-[#3AB8D6]'}`}
                      >
                        <action.icon size={14} className="mr-1" />
                        {action.label}
                      </Button>
                    ))}
                    <Button size="sm" variant="ghost" onClick={() => navigate(`/it/visits/${visit._id}`)}>
                      <Eye size={16} className="text-[#3AB8D6]" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleEdit(visit)}>
                      <PencilSimple size={16} className="text-[#E2B05E]" />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Create/Edit Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="bg-[#1a1d23] border-gray-700 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-[#E2B05E]">
                {editingVisit ? 'Editar Visita' : 'Nueva Visita Técnica'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-gray-300">Título de la Visita *</Label>
                <Input 
                  value={form.title || ''} 
                  onChange={(e) => updateForm('title', e.target.value)} 
                  placeholder="Ej: Mantenimiento preventivo - Equipos de oficina"
                  className="bg-[#12141a] border-gray-700 text-white" 
                  required 
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Cliente *</Label>
                  <Select value={form.client_id} onValueChange={(v) => updateForm('client_id', v)}>
                    <SelectTrigger className="bg-[#12141a] border-gray-700 text-white">
                      <SelectValue placeholder="Seleccionar cliente" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1d23] border-gray-700">
                      {clients.map(c => (
                        <SelectItem key={c._id} value={c._id}>{c.name || c.company}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Sucursal</Label>
                  <Select value={form.location_id} onValueChange={(v) => updateForm('location_id', v)}>
                    <SelectTrigger className="bg-[#12141a] border-gray-700 text-white">
                      <SelectValue placeholder="Seleccionar sucursal" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1d23] border-gray-700">
                      {locations.map(l => (
                        <SelectItem key={l._id} value={l._id}>{l.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Tipo de Visita</Label>
                  <Select value={form.tipo} onValueChange={(v) => updateForm('tipo', v)}>
                    <SelectTrigger className="bg-[#12141a] border-gray-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1d23] border-gray-700">
                      {visitTypes.map(vt => (
                        <SelectItem key={vt.code} value={vt.code}>{vt.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Prioridad</Label>
                  <Select value={form.priority} onValueChange={(v) => updateForm('priority', v)}>
                    <SelectTrigger className="bg-[#12141a] border-gray-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1d23] border-gray-700">
                      {priorities.map(p => (
                        <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Fecha Programada</Label>
                  <Input type="date" value={form.scheduled_date} onChange={(e) => updateForm('scheduled_date', e.target.value)} className="bg-[#12141a] border-gray-700 text-white" />
                </div>
                <div>
                  <Label className="text-gray-300">Hora</Label>
                  <Input type="time" value={form.scheduled_time} onChange={(e) => updateForm('scheduled_time', e.target.value)} className="bg-[#12141a] border-gray-700 text-white" />
                </div>
                <div>
                  <Label className="text-gray-300">Duración Estimada (min)</Label>
                  <Input type="number" value={form.duracion_estimada_min} onChange={(e) => updateForm('duracion_estimada_min', parseInt(e.target.value) || 60)} className="bg-[#12141a] border-gray-700 text-white" />
                </div>
                <div>
                  <Label className="text-gray-300">Recibe (nombre)</Label>
                  <Input value={form.recibe_nombre} onChange={(e) => updateForm('recibe_nombre', e.target.value)} className="bg-[#12141a] border-gray-700 text-white" />
                </div>
              </div>

              {/* Technicians */}
              <div>
                <Label className="text-gray-300 mb-2 block">Técnicos Asignados</Label>
                <div className="flex gap-2 mb-2">
                  <Select value={selectedTechId} onValueChange={setSelectedTechId}>
                    <SelectTrigger className="bg-[#12141a] border-gray-700 text-white flex-1">
                      <SelectValue placeholder="Seleccionar técnico" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1d23] border-gray-700">
                      {employees.map(e => (
                        <SelectItem key={e._id} value={e._id}>{e.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button type="button" onClick={addTechnician} className="bg-[#3AB8D6] text-black">
                    <Plus size={16} />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {form.technicians.map((tech) => {
                    const emp = employees.find(e => e._id === tech.employee_id);
                    return (
                      <Badge key={tech.employee_id} className="bg-[#3AB8D6]/20 text-[#3AB8D6] flex items-center gap-1 px-3 py-1">
                        {emp?.name || tech.employee_id}
                        <X size={12} className="cursor-pointer ml-1" onClick={() => removeTechnician(tech.employee_id)} />
                      </Badge>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label className="text-gray-300">Descripción</Label>
                <Textarea value={form.description} onChange={(e) => updateForm('description', e.target.value)} className="bg-[#12141a] border-gray-700 text-white" rows={3} />
              </div>
              <div>
                <Label className="text-gray-300">Notas</Label>
                <Textarea value={form.notes} onChange={(e) => updateForm('notes', e.target.value)} className="bg-[#12141a] border-gray-700 text-white" rows={2} />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="border-gray-600 text-gray-300">
                  Cancelar
                </Button>
                <Button type="submit" className="bg-[#E2B05E] hover:bg-[#c9993f] text-black">
                  {editingVisit ? 'Actualizar' : 'Crear'} Visita
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Modal de Administración de Tipos y Prioridades */}
        <Dialog open={showAdminModal} onOpenChange={setShowAdminModal}>
          <DialogContent className="bg-[#1a1d23] border-gray-700 text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-[#E2B05E] flex items-center gap-2">
                <PencilSimple size={20} /> Gestión de Catálogos
              </DialogTitle>
            </DialogHeader>

            <div className="flex gap-2 mb-4">
              <Button 
                variant={adminCategory === 'visit_type' ? 'default' : 'outline'} 
                onClick={() => { setAdminCategory('visit_type'); loadAdminItems('visit_type'); }}
                className={adminCategory === 'visit_type' ? 'bg-[#E2B05E] text-black' : 'border-gray-600'}
              >
                Tipos de Visita
              </Button>
              <Button 
                variant={adminCategory === 'priority' ? 'default' : 'outline'} 
                onClick={() => { setAdminCategory('priority'); loadAdminItems('priority'); }}
                className={adminCategory === 'priority' ? 'bg-[#E2B05E] text-black' : 'border-gray-600'}
              >
                Prioridades
              </Button>
            </div>

            {/* Formulario para agregar nuevo */}
            <div className="flex gap-2 mb-4">
              <Input 
                placeholder={adminCategory === 'visit_type' ? "Nuevo tipo de visita..." : "Nueva prioridad..."}
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                className="bg-[#12141a] border-gray-700"
                onKeyDown={(e) => e.key === 'Enter' && handleCreateItem()}
              />
              <Button onClick={handleCreateItem} className="bg-[#3AB8D6] text-black">
                <Plus size={16} className="mr-1" /> Agregar
              </Button>
            </div>

            {/* Lista de items */}
            <div className="max-h-[400px] overflow-y-auto border border-gray-700 rounded-lg">
              {adminLoading ? (
                <div className="p-8 text-center text-gray-400">Cargando...</div>
              ) : adminItems.length === 0 ? (
                <div className="p-8 text-center text-gray-400">No hay elementos. Agrega el primero.</div>
              ) : (
                adminItems.map((item, index) => {
                  const itemId = item._id || item.id;
                  const itemName = item.name || item.nombre || '';
                  const isEditing = editingItem && (editingItem._id || editingItem.id) === itemId;

                  return (
                    <div key={itemId || index} className="flex items-center justify-between p-3 border-b border-gray-700 last:border-b-0 hover:bg-[#12141a]">
                      {isEditing ? (
                        <div className="flex-1 flex gap-2">
                          <Input 
                            value={editName} 
                            onChange={(e) => setEditName(e.target.value)} 
                            className="bg-[#12141a] border-gray-600" 
                            autoFocus
                          />
                          <Button size="sm" onClick={handleUpdateItem} className="bg-green-600">Guardar</Button>
                          <Button size="sm" variant="outline" onClick={cancelEdit}>Cancelar</Button>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-3">
                            <span className="text-white font-medium">{itemName}</span>
                            {item.order !== undefined && (
                              <Badge variant="outline" className="text-xs">Orden: {item.order}</Badge>
                            )}
                            {item.is_active === false && <Badge className="bg-red-500/20 text-red-400">Inactivo</Badge>}
                          </div>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => startEditItem(item)}>
                              <PencilSimple size={14} />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDeleteItem(item)} className="text-red-400 hover:text-red-500">
                              <X size={14} />
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            <div className="text-xs text-gray-500 mt-2">
              Los cambios se reflejan inmediatamente en los formularios de visitas. Los elementos desactivados no aparecen en los dropdowns nuevos.
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
